import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import {
	CHART_COUNT_METRIC_VALUE,
	isChartDateLikePropertyType,
	type ChartPresentationVariant,
} from '../chart/canvasChartData';
import { EntityType, type ChartEntry, type Repository } from '../types';
import ChartWhereFilterBuilder from './ChartWhereFilterBuilder';
import { useAppSelector } from '../hooks';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';
import { selectIsAuthenticated } from '../containers/authSlice';
import { sortEntryByRank } from '../common/util';
import { METRIC_PROPERTY_DEFINITION_TYPES, X_AXIS_PROPERTY_DEFINITION_TYPES } from './AddChartDialog';

function formatChartTargetForInput(v: number | null | undefined): string {
	if (v === null || v === undefined || !Number.isFinite(v)) {
		return '';
	}
	return String(v);
}

function parseOptionalChartTargetInput(raw: string): number | null {
	const t = raw.trim();
	if (t === '') {
		return null;
	}
	const n = Number(t);
	return Number.isFinite(n) ? n : null;
}

export interface EditChartDialogProps {
	open: boolean;
	chart: ChartEntry | null;
	repositories: Repository[];
	onClose: () => void;
	onSubmit: (payload: {
		id: string;
		repositoryId: string;
		title: string;
		where?: string | null;
		includeArchived: boolean;
		metric?: string;
		xAxis?: string;
		xAxisTimeScale?: 'D' | 'W' | 'M' | null;
		variant?: ChartPresentationVariant;
		targetA?: number | null;
		targetB?: number | null;
	}) => void;
}

const EditChartDialog: React.FC<EditChartDialogProps> = ({
	open,
	chart,
	repositories,
	onClose,
	onSubmit,
}) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [title, setTitle] = useState('');
	const [where, setWhere] = useState('');
	const [includeArchived, setIncludeArchived] = useState(true);
	const [metricSlug, setMetricSlug] = useState('');
	const [xAxisSlug, setXAxisSlug] = useState('');
	const [xAxisTimeScale, setXAxisTimeScale] = useState<'D' | 'W' | 'M' | ''>('');
	const [variant, setVariant] = useState<ChartPresentationVariant>('line');
	const [targetAStr, setTargetAStr] = useState('');
	const [targetBStr, setTargetBStr] = useState('');
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { data: repositoryById, isFetching: isRepositoryFetching } = useGetRepositoryByIdQuery(
		repositoryId,
		{ skip: !open || !repositoryId || !isAuthenticated }
	);
	const localPropertyDefinitions = useMemo(() => {
		return allPropertyDefinitions.filter((def) => def.parentId === repositoryId).sort(sortEntryByRank);
	}, [allPropertyDefinitions, repositoryId]);
	const propertyDefinitionsSource = useMemo(
		() =>
			(isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions) as Array<{
				slug: string;
				label?: string;
				type: EntityType;
				required?: boolean;
				targetRepositoryId?: string;
			}>,
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions]
	);
	const propertyOptions = useMemo(
		() =>
			propertyDefinitionsSource.map((property) => ({
				slug: property.slug,
				label: property.label?.trim() ? property.label : property.slug,
				type: property.type,
				required: (property as { required?: boolean }).required === true,
				targetRepositoryId:
						property.type === EntityType.PropertyDefinitionBelongsTo
							? String(
								(property as { targetRepositoryId?: string }).targetRepositoryId ?? '',
							).trim()
							: '',
			})),
		[propertyDefinitionsSource]
	);
	const metricOptions = useMemo(
		() =>
			propertyOptions.filter((option) =>
				METRIC_PROPERTY_DEFINITION_TYPES.includes(option.type as EntityType)
			),
		[propertyOptions]
	);
	const xAxisOptions = useMemo(
		() =>
			propertyOptions.filter((option) =>
				X_AXIS_PROPERTY_DEFINITION_TYPES.includes(option.type as EntityType)
			),
		[propertyOptions]
	);

	const selectedXAxisType = useMemo(
		() => xAxisOptions.find((p) => p.slug === xAxisSlug)?.type,
		[xAxisOptions, xAxisSlug]
	);
	const showTimeScale = isChartDateLikePropertyType(selectedXAxisType);
	const showTimeScaleControl = showTimeScale && (variant === 'line' || variant === 'bar');
	const showHeatmapVariant = showTimeScale;
	const showLineBarTargets =
		(variant === 'line' || variant === 'bar') && Boolean(xAxisSlug.trim());
	const presentationVariantOptions = useMemo(() => {
		const base: { value: ChartPresentationVariant; label: string }[] = [
			{ value: 'line', label: 'Line' },
			{ value: 'bar', label: 'Bar' },
		];
		if (showHeatmapVariant) {
			base.push({ value: 'heatmap', label: 'Heatmap' });
		}
		return base;
	}, [showHeatmapVariant]);

	useLayoutEffect(() => {
		if (!open || !chart) {
			return;
		}
		setRepositoryId(chart.repositoryId ?? repositories[0]?.id ?? '');
		setTitle(chart.title ?? '');
		setWhere(chart.where ?? '');
		setIncludeArchived(chart.includeArchived !== false);
		setMetricSlug((chart.metric ?? '').trim());
		setXAxisSlug((chart.xAxis ?? '').trim());
		const saved = (chart.xAxisTimeScale ?? '').trim();
		const vr = (chart.variant ?? '').trim();
		const v = vr === 'bar' || vr === 'heatmap' ? vr : 'line';
		setVariant(v);
		if (v === 'heatmap') {
			setXAxisTimeScale('');
		} else {
			setXAxisTimeScale(saved === 'D' || saved === 'W' || saved === 'M' ? saved : '');
		}
		setTargetAStr(formatChartTargetForInput(chart.targetA));
		setTargetBStr(formatChartTargetForInput(chart.targetB));
	}, [open, chart, repositories]);

	useEffect(() => {
		if (variant === 'heatmap') {
			setXAxisTimeScale('');
			setTargetAStr('');
			setTargetBStr('');
		}
	}, [variant]);

	useEffect(() => {
		if (variant === 'heatmap' && !showHeatmapVariant) {
			setVariant('line');
		}
	}, [variant, showHeatmapVariant]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!chart) {
			return;
		}
		const titleTrimmed = title.trim();
		const whereTrimmed = where.trim();
		if (!repositoryId || !titleTrimmed) {
			return;
		}
		const variantOut = variant === 'heatmap' && !showHeatmapVariant ? 'line' : variant;
		const canUseTargets = variantOut === 'line' || variantOut === 'bar';
		onSubmit({
			id: chart.id,
			repositoryId,
			title: titleTrimmed,
			where: whereTrimmed !== '' ? whereTrimmed : null,
			includeArchived,
			metric: metricSlug || undefined,
			xAxis: xAxisSlug || undefined,
			xAxisTimeScale: showTimeScaleControl && xAxisTimeScale
				? xAxisTimeScale
				: null,
			variant: variantOut,
			targetA: canUseTargets ? parseOptionalChartTargetInput(targetAStr) : null,
			targetB: canUseTargets ? parseOptionalChartTargetInput(targetBStr) : null,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogTitle>Edit chart</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField
							label="Title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							fullWidth
							required
							autoFocus
							size="small"
						/>
						<FormControl fullWidth required size="small">
							<InputLabel id="edit-chart-repository-label">Collection</InputLabel>
							<Select
								labelId="edit-chart-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => {
									setRepositoryId(String(e.target.value));
									setMetricSlug('');
									setXAxisSlug('');
									setXAxisTimeScale('');
									setTargetAStr('');
									setTargetBStr('');
									setWhere('');
									setIncludeArchived(true);
									setVariant('line');
								}}
							>
								{repositories.map((r) => (
									<MenuItem key={r.id} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{chart && open && (
							<ChartWhereFilterBuilder
								chartId={chart.id}
								initialWhere={where}
								onChange={setWhere}
								properties={propertyOptions}
								disabled={!repositoryId || isRepositoryFetching}
								includeArchived={includeArchived}
								onIncludeArchivedChange={setIncludeArchived}
								shouldAskAboutRelativeTime={true}
							/>
						)}
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>Presentation</Typography>
						<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching} required>
							<InputLabel id="edit-chart-metric-label">Metric</InputLabel>
							<Select
								labelId="edit-chart-metric-label"
								label="Metric"
								value={metricSlug}
								onChange={(e) => setMetricSlug(String(e.target.value))}
							>
								{isRepositoryFetching ? (
									<MenuItem value="" disabled>
										Loading properties...
									</MenuItem>
								) : (
									[{ slug: CHART_COUNT_METRIC_VALUE, label: 'Number of entries' }, ...metricOptions].map((option) => (
										<MenuItem key={option.slug} value={option.slug}>
											{option.label}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
						<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching} required>
							<InputLabel id="edit-chart-x-axis-label">X-axis</InputLabel>
							<Select
								labelId="edit-chart-x-axis-label"
								label="X-axis"
								value={xAxisSlug}
								onChange={(e) => {
									setXAxisSlug(String(e.target.value));
									setXAxisTimeScale('');
									setTargetAStr('');
									setTargetBStr('');
								}}
							>
								{isRepositoryFetching ? (
									<MenuItem value="" disabled>
										Loading properties...
									</MenuItem>
								) : (
									xAxisOptions.map((option) => (
										<MenuItem key={option.slug} value={option.slug}>
											{option.label}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
						{Boolean(xAxisSlug) && (
							<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching}>
								<InputLabel id="edit-chart-variant-label">Chart type</InputLabel>
								<Select
									labelId="edit-chart-variant-label"
									label="Chart type"
									value={
										presentationVariantOptions.some((o) => o.value === variant)
											? variant
											: 'line'
									}
									onChange={(e) =>
										setVariant(String(e.target.value) as ChartPresentationVariant)
									}
								>
									{presentationVariantOptions.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{showTimeScaleControl && (
							<FormControl fullWidth size="small" disabled={!repositoryId || isRepositoryFetching}>
								<InputLabel id="edit-chart-time-scale-label">Time scale</InputLabel>
								<Select
									labelId="edit-chart-time-scale-label"
									label="Time scale"
									value={xAxisTimeScale}
									onChange={(e) => setXAxisTimeScale(String(e.target.value) as 'D' | 'W' | 'M' | '')}
								>
									<MenuItem value="">None (default)</MenuItem>
									<MenuItem value="D">Daily</MenuItem>
									<MenuItem value="W">Weekly</MenuItem>
									<MenuItem value="M">Monthly</MenuItem>
								</Select>
							</FormControl>
						)}
						{showLineBarTargets && (
							<>
								<TextField
									label="Target A"
									value={targetAStr}
									onChange={(e) => setTargetAStr(e.target.value)}
									fullWidth
									size="small"
									type="text"
									inputMode="decimal"
									disabled={!repositoryId || isRepositoryFetching}
								/>
								<TextField
									label="Target B"
									value={targetBStr}
									onChange={(e) => setTargetBStr(e.target.value)}
									fullWidth
									size="small"
									type="text"
									inputMode="decimal"
									disabled={!repositoryId || isRepositoryFetching}
								/>
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!repositoryId || !title.trim()}>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditChartDialog;
